/**
 * What a door adapter's write gate MEANS, for the panel that arms it and
 * for the Devices title row and Needs attention strip.
 */
import type { DoorWriteGate } from '../../api/v1-client';
import type { StatusKind } from '../../design-system/tokens';

export type DoorGateStatusKind =
  /** Validated, live and armed: this adapter may open the doors it owns. */
  | 'armed'
  /** Tier 1 passed, tier 2 has not. Nobody has armed this adapter. */
  | 'disarmed'
  /** Not deployed `--live`. Arming it would change nothing at all. */
  | 'shadow'
  /** Tier 1 failed: the vendor device list did not resolve into doors. */
  | 'unvalidated'
  /** The gate has not been read yet, or could not be. NOT disarmed. */
  | 'unknown'
  /** The api-server answered 404: this deployment serves no door gate. */
  | 'unsupported'
  /**
   * The gate read succeeded and named no adapter for what was asked —
   * an empty list for the site, or no adapter owning one door. Kept
   * apart from every verdict above because those are readings OF an
   * adapter and this one says there is no adapter to read.
   */
  | 'no_adapter';

export interface DoorGateStatus {
  readonly kind: DoorGateStatusKind;
  readonly chip: StatusKind;
  /** Two words at most — this sits beside a heading. */
  readonly label: string;
  /** What is true, in one sentence. */
  readonly meaning: string;
  /** What the person reading it can do about it — including
   *  "nothing from here, and here is what it takes instead". */
  readonly action: string;
  /** The adapter's own words, passed through when it has any. */
  readonly detail: string;
}

export const UNKNOWN: DoorGateStatus = {
  kind: 'unknown',
  chip: 'offline',
  label: 'Unknown',
  // Said carefully, for the reason the lift's equivalent is: a console
  // that has never been told must not render as one that was told no.
  meaning:
    'The console has not been able to read this site’s door write gates, so it cannot say whether any door adapter is armed.',
  action:
    'Check that the api-server is reachable. An adapter may well be armed and working — this says nothing either way.',
  detail: '',
};

const UNKNOWN_STALE: DoorGateStatus = {
  kind: 'unknown',
  chip: 'offline',
  label: 'Stale',
  meaning:
    'The last write gate this adapter published is too old to present as current, so the console will not say whether it is armed.',
  action:
    'Check the door adapter and the site worker are running. The adapter republishes its gate every 5 s, so a reading this old means one of them stopped — and it may have stopped while armed.',
  detail: '',
};

export const UNSUPPORTED: DoorGateStatus = {
  kind: 'unsupported',
  chip: 'offline',
  label: 'Not available',
  meaning:
    'This deployment’s api-server does not publish door write gates, so arm state cannot be read from this console at all.',
  action:
    'Arm on the adapter’s own control port until an api-server that serves the gate is deployed here. This says nothing about whether the adapter is armed.',
  detail: '',
};

export const NO_ADAPTER: DoorGateStatus = {
  kind: 'no_adapter',
  chip: 'offline',
  label: 'No adapter',
  meaning:
    'The gate read succeeded and no door adapter at this site published one, so there is nothing here to arm.',
  action:
    'A site whose doors are driven by something other than a gated adapter lives here permanently, and that is not a fault. If this site has a door adapter, check that it is running and publishing.',
  detail: '',
};

function unvalidated(gate: DoorWriteGate): DoorGateStatus {
  return {
    kind: 'unvalidated',
    chip: 'error',
    label: 'Not validated',
    meaning:
      'The adapter’s vendor device list has not resolved into addressable doors, so it is refusing to open any of them — armed or not.',
    // Tier 1 belongs to the adapter and nothing here second-guesses it
    // (invariant I3). The console's only job is to not offer a button
    // whose one outcome is a refusal.
    action:
      'Fix the device list at the adapter until its inventory resolves. Arming cannot be forced from here, and arming while this holds would change nothing.',
    detail: gate.inventory_detail,
  };
}

function shadow(gate: DoorWriteGate): DoorGateStatus {
  return {
    kind: 'shadow',
    chip: 'warning',
    label: 'Shadow mode',
    meaning: gate.armed
      ? 'This adapter is ARMED but is not deployed with `live: true`, so it sends nothing to a real door and reports every door it owns as offline. The arm is having no effect.'
      : 'This adapter is not deployed with `live: true`, so it observes doors and commands none. Arming it would have no effect until it is deployed live.',
    // Deliberately says OPEN and not "commands". A close is a
    // RELEASE-class write at the adapter and clears every tier, shadow
    // mode included, so that a process being stopped can always let go
    // of a door it is holding. Telling an operator that closing is
    // blocked too would be wrong in the direction that matters.
    action:
      'Deploying live is a reviewed change to the adapter’s descriptor, not a switch on this screen. Until it ships, Open is refused at the adapter and the refusal is written only to its log. Closing a door still works.',
    detail: gate.inventory_detail,
  };
}

function disarmed(gate: DoorWriteGate): DoorGateStatus {
  return {
    kind: 'disarmed',
    chip: 'warning',
    label: 'Not armed',
    // The state a supervisor meets most (after every restart), so in
    // hotel words (P8.7, 2026-10-31): it said "adapter's inventory" and
    // "for RMF". The ops detail is still `detail` below.
    meaning:
      'Nobody has armed this door system, so it will not open a door for a robot or for you. It comes back like this after every restart.',
    action: 'A supervisor must arm it. Closing a door always works.',
    detail: gate.inventory_detail,
  };
}

function armed(gate: DoorWriteGate): DoorGateStatus {
  return {
    kind: 'armed',
    chip: 'active',
    label: 'Armed',
    meaning: gate.armed_by
      ? `Armed by ${gate.armed_by}: this adapter may open every door it owns.`
      : 'Armed: this adapter may open every door it owns.',
    action:
      'Disarm to stand it down. Closing a door still works afterwards, so disarming never leaves a door latched open on this account.',
    detail: gate.inventory_detail,
  };
}

/**
 * What one adapter's gate MEANS, derived here and nowhere else.
 *
 * The chip, the sentences and the Arm button's disabled state all come
 * off this one verdict, which is the lesson `lift-dispatch-status.ts`
 * paid for: when the chip and the tooltip were derived separately, the
 * chip honestly said the gate could not be read while the tooltip an
 * inch below it blamed a failed inventory nobody had ever received.
 *
 * THE ORDER MATCHES `WriteGate.check` IN THE ADAPTER — inventory,
 * then shadow, then armed — so the sentence this panel shows is the
 * same sentence the adapter would raise if the operator pressed Open.
 * Reordering these makes the console explain a refusal with a cause
 * the adapter did not use.
 *
 * Its own module, as the lift's `lift-dispatch-status.ts` is, because a
 * second surface reads it: the Devices title row and Needs attention.
 */
export function doorGateStatus(gate: DoorWriteGate): DoorGateStatus {
  // STALE OUTRANKS EVERY OTHER READING, and is checked first for that
  // reason. The collection read returns a gate too old to present as
  // current rather than dropping it -- dropping would render as "no
  // such adapter" about an adapter that may be armed -- so the console
  // is the last place that can refuse to state it as fact. An adapter
  // that died while armed keeps its last document in the storage
  // manager for as long as the cache lives, and rendering that as
  // `armed` is a console calling a floor's doors armed hours after the
  // reboot that disarmed them.
  //
  // It resolves to UNKNOWN, not to disarmed: "we cannot see the gate"
  // and "we can see it and it is off" are different facts, and only one
  // of them is a claim about an adapter. UNKNOWN also blocks Arm
  // (`armBlockedReason`) while leaving Disarm drawn on the raw flag,
  // which is invariant I2.
  if (gate.stale) return UNKNOWN_STALE;
  if (!gate.inventory_ok) return unvalidated(gate);
  if (gate.shadow) return shadow(gate);
  if (!gate.armed) return disarmed(gate);
  return armed(gate);
}

/**
 * Why the Arm control is refused, in the words to put on it — or
 * `undefined` when it is not refused, which is the ONLY thing that may
 * enable the button.
 *
 * Note what is not here: no fallback that treats an unreadable or
 * shadowed gate as permission. Every branch that is not 'disarmed'
 * returns a sentence, because the one thing worse than an operator who
 * cannot arm a door is an operator who believes they have.
 */
export function armBlockedReason(status: DoorGateStatus): string | undefined {
  switch (status.kind) {
    case 'disarmed':
      // Validated, live, and nobody has armed it: the one state in
      // which arming is a thing a person can do from here.
      return undefined;
    case 'unvalidated':
      return 'Blocked by the adapter’s inventory: it refuses to open a door until its vendor device list resolves.';
    case 'shadow':
      return 'Shadow mode: this adapter is not deployed with `live: true`, so arming it would have no effect. It would still send nothing to a real door, and Open would still be refused.';
    case 'armed':
      return 'This adapter is already armed.';
    case 'unknown':
      return 'The console cannot read this adapter’s write gate, so it will not offer to arm it. This does not mean it is disarmed — it means nothing here knows either way.';
    case 'unsupported':
      return 'This deployment does not publish door write gates, so arm state cannot be read or changed from this console.';
    case 'no_adapter':
      return 'No door adapter published a gate here, so there is nothing to arm.';
  }
}

/**
 * Whether the console holds an actual reading of a gate, as opposed to
 * a verdict whose content is "I cannot tell".
 *
 * Spends itself on one sentence: the one shown after an arm is
 * accepted. The adapter's own refusal — SHADOW MODE, "writes disabled
 * — the MEGAZO device list has not resolved" — goes to its log and
 * reaches no browser, so the only evidence a console can offer that an
 * arm took is the gate changing underneath it. Where there is no gate
 * reading there is no evidence, and promising that "the state above
 * will update" would be a promise nothing can keep.
 */
export function gateIsReadable(status: DoorGateStatus): boolean {
  return status.kind !== 'unknown' && status.kind !== 'unsupported' && status.kind !== 'no_adapter';
}
